import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Download, Terminal, ArrowRight, BookOpen, ChevronRight,
  CheckCircle2, Send,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import VideoAd from "@/components/VideoAd";

const VIDEO_SMB = "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/1np457in_DARA%20%E2%80%94%20Enterprise%20AI%20Data%20Monetization%20%281%29.mp4";

// Phase 36.7 — Founder's Guide lead-capture funnel
// Public landing page (no auth) that gates the existing PDF behind a name+email form.

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

const HIGHLIGHTS = [
  { label: "11 jurisdiction playbooks", body: "Mining, Shipping, Banking, Manufacturing — auto-applied to your data." },
  { label: "Hedge Co-Pilot", body: "DARA-grounded hedging programmes citing real LME / SGX / COMEX contracts." },
  { label: "Commodity Desk", body: "9 live benchmarks (IODEX, LME, LBMA) with bull / base / bear outlook on demand." },
  { label: "Cash-Flow Forecast", body: "30/60/90-day collection projection from Xero or any CSV — no integration required." },
  { label: "Industrial Adapter (preview)", body: "SCADA / OPC UA / MODBUS gateway streaming live tonnes per shift." },
  { label: "DARA Replies", body: "Reply to any DARA email with a question; DARA answers in under 15 seconds." },
];

export default function FoundersGuide() {
  usePageview("/founders-guide");
  const [form, setForm] = useState({ name: "", email: "", email_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    if (!form.email_consent) {
      toast.error("Tick the consent box to receive the guide by email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/founders-guide-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, visitor_session: getVisitorSession() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Submit failed");
      setDownloadUrl(`${BACKEND}${data.download_url}`);
      toast.success("Your guide is ready.");
    } catch (e) {
      toast.error(e.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="founders-guide-landing">
      {/* HEADER */}
      <header className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <Link to="/login"
            className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">
            LOGIN
          </Link>
        </div>
      </header>

      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* LEFT — pitch (appears AFTER the form on mobile) */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
              // FOUNDER'S GUIDE · v1.51
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Every DARA module,<br/>
              <span className="text-[#FFB000]">explained in 80 pages.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mt-6 leading-relaxed">
              The DARA OS Founder's Guide is the canonical reference covering every feature
              shipped to date — from cash-flow forecasting to the Hedging Co-Pilot to the 11
              jurisdiction playbooks. Designed for investor demos, new-team onboarding, and
              prospect deep-dives.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {HIGHLIGHTS.map((h, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5">
                  <ChevronRight className="w-3.5 h-3.5 text-[#FFB000] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">{h.label}</div>
                    <div className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{h.body}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Phase 25.13an — SMB video ad. 23s problem/solution pitch
                tailored to POS-driven merchants. Lives below the highlights
                grid so visitors see the proof points before the video. */}
            <div className="mt-8" data-testid="founders-guide-video-section">
              <VideoAd
                src={VIDEO_SMB}
                label="// FOUNDER · 23s · WATCH FIRST"
                caption="// FOR POS-DRIVEN MERCHANTS · 11 JURISDICTION PLAYBOOKS · BOOST REVENUE WITH DARA"
                testId="founders-guide-video"
              />
            </div>
          </div>

          {/* RIGHT — capture form (appears FIRST on mobile so it's above the fold) */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            {/* Mobile-only micro pitch above the form so the page doesn't feel decapitated */}
            <div className="lg:hidden mb-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-2">
                // FOUNDER'S GUIDE · v1.51
              </div>
              <h1 className="font-heading font-black text-3xl tracking-tight leading-[1.05]">
                The DARA Founder's Guide.
                <span className="block text-[#FFB000]">80 pages. Free.</span>
              </h1>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                The canonical reference covering every module — for investor demos, onboarding, and deep-dives.
              </p>
            </div>

            {!downloadUrl && (
              <form
                onSubmit={submit}
                className="border border-zinc-800 bg-zinc-950/40 p-6"
                data-testid="founders-guide-form"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" /> // FREE DOWNLOAD · 30 SECONDS
                </div>
                <div className="font-heading font-black text-2xl tracking-tight">Get the Founder's Guide</div>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                  PDF, ~3MB. Updated with every release. Two fields, no spam.
                </p>

                <div className="space-y-3 mt-5">
                  <Input label="NAME *" testid="fg-name"
                    value={form.name}
                    onChange={v => setForm(f => ({ ...f, name: v }))}
                    placeholder="Aisha Patel" />
                  <Input label="WORK EMAIL *" type="email" testid="fg-email"
                    value={form.email}
                    onChange={v => setForm(f => ({ ...f, email: v }))}
                    placeholder="aisha@yourcompany.com" />
                </div>

                {/* Consent BEFORE submit — natural reading order */}
                <label
                  className="mt-5 flex items-start gap-3 cursor-pointer"
                  data-testid="fg-consent-row"
                >
                  <input
                    type="checkbox"
                    checked={form.email_consent}
                    onChange={(e) => setForm({ ...form, email_consent: e.target.checked })}
                    data-testid="fg-consent-checkbox"
                    className="mt-1 accent-[#FFB000] w-4 h-4 flex-shrink-0 cursor-pointer"
                  />
                  <div className="text-xs text-zinc-300 leading-relaxed">
                    <strong className="text-white">
                      Email me the guide + one short follow-up two weeks later.
                    </strong>{" "}
                    <span className="text-zinc-500">
                      One-click unsubscribe in every email.{" "}
                      <Link to="/consent" target="_blank" className="text-[#FFB000] hover:underline">
                        Email policy
                      </Link>.
                    </span>
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="fg-submit"
                  className="mt-5 w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? "PREPARING…" : "GET THE GUIDE"}
                </button>

                {/* Phase 25.13ah — Anonymous lighter-touch path for the
                    HOT-but-not-converting cohort. The 80-page guide
                    stays gated; this offers a 1-page summary with no
                    email required, addressing the "12 HOT sessions, 0
                    leads" leak. */}
                <a
                  href={`${BACKEND}/api/public/overview-one-pager`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fg-anon-pdf"
                  className="mt-4 w-full border border-zinc-700 hover:border-[#FFB000]/50 hover:bg-zinc-950 text-zinc-300 hover:text-[#FFB000] font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 flex items-center justify-center gap-2"
                >
                  <Download className="w-3 h-3" />
                  Skip the form — get the 1-pager (no email)
                </a>

                <div className="font-mono text-[9px] text-zinc-600 mt-4 leading-relaxed">
                  We'll never share your email. Consent timestamp + IP logged for audit.
                </div>
              </form>
            )}

            {downloadUrl && (
              <div
                className="border border-[#00E676]/40 bg-[#00E676]/5 p-6 flex flex-col items-start gap-4"
                data-testid="founders-guide-success"
              >
                <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
                <div>
                  <div className="font-heading font-black text-2xl tracking-tight">Your guide is ready.</div>
                  <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
                    The Founder's Guide is a PDF — opens in a new tab and downloads
                    immediately. Bookmark this page if you want to grab it again later.
                  </p>
                </div>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="fg-download-btn"
                  className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 flex items-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" /> DOWNLOAD PDF
                </a>
                <Link
                  to="/signup"
                  className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
                >
                  → Or jump straight in: spin up a workspace <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between flex-wrap gap-4">
          <div className="font-mono text-[10px] text-zinc-600">
            DARA OS — Enterprise data-to-revenue terminal · daraos.ai
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">HOME</Link>
            <Link to="/industrial" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">INDUSTRIAL</Link>
            <Link to="/login" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">LOGIN</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
